// Slice 11 — shared chart primitives (Recharts wrappers).
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend, LabelList,
} from "recharts";

const COLORS = [
  "#6366f1", "#22c55e", "#f59e0b", "#ef4444", "#06b6d4",
  "#a855f7", "#ec4899", "#84cc16", "#0ea5e9", "#f97316",
];

const tooltipStyle = {
  contentStyle: {
    background: "hsl(var(--card))",
    border: "1px solid hsl(var(--border))",
    borderRadius: "8px",
    fontSize: "12px",
  },
  labelStyle: { color: "hsl(var(--muted-foreground))", fontSize: "11px" },
};

export function LineChartC({ data, xKey, yKeys = [], height = 260 }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 8, right: 16, bottom: 0, left: -10 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.4} />
        <XAxis dataKey={xKey} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
        <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
        <Tooltip {...tooltipStyle} />
        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} />
        {yKeys.map((y, i) => (
          <Line
            key={y.id}
            type="monotone"
            dataKey={y.id}
            name={y.label}
            stroke={COLORS[i % COLORS.length]}
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

export function BarChartC({ data, xKey, yKeys = [], height = 260, stacked = false }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 12, right: 16, bottom: 0, left: -10 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.4} />
        <XAxis dataKey={xKey} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
        <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
        <Tooltip {...tooltipStyle} />
        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} />
        {yKeys.map((y, i) => (
          <Bar
            key={y.id}
            dataKey={y.id}
            name={y.label}
            fill={COLORS[i % COLORS.length]}
            radius={[6, 6, 0, 0]}
            stackId={stacked ? "a" : undefined}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

export function PieChartC({ data, nameKey, valueKey, height = 260, showLabels = true }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Tooltip {...tooltipStyle} />
        <Pie
          data={data}
          nameKey={nameKey}
          dataKey={valueKey}
          outerRadius={90}
          innerRadius={50}
          paddingAngle={2}
          stroke="hsl(var(--background))"
          strokeWidth={2}
        >
          {data.map((_, i) => (
            <Cell key={i} fill={COLORS[i % COLORS.length]} />
          ))}
          {showLabels && <LabelList dataKey={valueKey} position="outside" style={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />}
        </Pie>
        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

// Universal report renderer — picks a chart based on the report spec.
export function ReportChart({ kind, columns = [], rows = [], height }) {
  if (!rows.length) {
    return (
      <div className="h-[260px] flex items-center justify-center text-sm text-muted-foreground italic">
        No data in the selected range.
      </div>
    );
  }
  const dims = columns.filter((c) => c.kind === "dimension");
  const meas = columns.filter((c) => c.kind === "measure");
  const xKey = dims[0]?.id;
  if (kind === "pie" && dims[0] && meas[0]) {
    return <PieChartC data={rows} nameKey={dims[0].id} valueKey={meas[0].id} height={height} />;
  }
  if (kind === "line" && xKey && meas.length) {
    return <LineChartC data={rows} xKey={xKey} yKeys={meas.map((m) => ({ id: m.id, label: m.label }))} height={height} />;
  }
  return <BarChartC data={rows} xKey={xKey || meas[0]?.id} yKeys={meas.map((m) => ({ id: m.id, label: m.label }))} height={height} />;
}
